/**
 * File Cache Service
 * Stores downloaded SharePoint files in memory for quick re-access
 */

interface CachedFile {
  blob: Blob;
  downloadedAt: Date;
}

class FileCache {
  private cache: Map<string, CachedFile> = new Map();
  private maxSize: number = 500 * 1024 * 1024; // 500 MB max cache
  private currentSize: number = 0;

  /**
   * Add file to cache
   */
  set(path: string, blob: Blob): void {
    // Check if we need to clear cache
    if (this.currentSize + blob.size > this.maxSize) {
      console.log('Cache full, clearing old entries...');
      this.clearOldest();
    }

    this.cache.set(path, {
      blob,
      downloadedAt: new Date(),
    });

    this.currentSize += blob.size;
    console.log(`📦 Cached file: ${path} (${this.formatSize(blob.size)}). Total cache: ${this.formatSize(this.currentSize)}`);
  }

  /**
   * Get file from cache
   */
  get(path: string): Blob | null {
    const cached = this.cache.get(path);
    if (cached) {
      console.log(`✅ Cache HIT: ${path}`);
      return cached.blob;
    }
    console.log(`❌ Cache MISS: ${path}`);
    return null;
  }

  /**
   * Check if file is cached
   */
  has(path: string): boolean {
    return this.cache.has(path);
  }

  /**
   * Clear oldest entries to make space
   */
  private clearOldest(): void {
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].downloadedAt.getTime() - b[1].downloadedAt.getTime());

    // Remove oldest 25% of cache
    const toRemove = Math.ceil(entries.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      const [path, cached] = entries[i];
      this.cache.delete(path);
      this.currentSize -= cached.blob.size;
    }

    console.log(`🗑️ Cleared ${toRemove} old cache entries`);
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.currentSize = 0;
    console.log('🗑️ Cache cleared');
  }

  /**
   * Format file size for logging
   */
  private formatSize(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  }

  /**
   * Get cache statistics
   */
  getStats(): { count: number; size: string; maxSize: string } {
    return {
      count: this.cache.size,
      size: this.formatSize(this.currentSize),
      maxSize: this.formatSize(this.maxSize),
    };
  }
}

// Singleton instance
export const fileCache = new FileCache();










